import type { ReactNode } from 'react';
import { Box, type BoxProps, Flex, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Tone = 'brand' | 'warning';

type Props = BoxProps & {
  tone?: Tone;
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
};

const TONE_STYLES: Record<Tone, { background: string; border: string; icon: ReactNode }> = {
  brand: {
    background: 'brand.0',
    border: 'var(--mantine-color-brand-1)',
    icon: <IconSparkles size={16} color="var(--mantine-color-brand-6)" aria-hidden />,
  },
  warning: {
    background: 'warning.0',
    border: 'var(--mantine-color-warning-3)',
    icon: <IconAlertCircle size={16} color="var(--mantine-color-warning-7)" aria-hidden />,
  },
};

export function DefaultTemplateNotice({
  tone = 'brand',
  title,
  children,
  actions,
  ...boxProps
}: Props) {
  const toneStyles = TONE_STYLES[tone];

  return (
    <Box
      {...boxProps}
      bg={toneStyles.background}
      p="sm"
      style={{ borderRadius: 12, border: `1px solid ${toneStyles.border}` }}
    >
      <Flex gap="xs" align="flex-start">
        <ThemeIcon variant="white" radius="xl" size={28} style={{ flexShrink: 0 }}>
          {toneStyles.icon}
        </ThemeIcon>
        <Stack gap="xxxs" style={{ minWidth: 0 }}>
          {title && (
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
              {title}
            </Text>
          )}
          <Text size="sm" c="gray.8">
            {children}
          </Text>
          {actions && (
            <Flex gap="xs" mt="xxs" wrap="wrap">
              {actions}
            </Flex>
          )}
        </Stack>
      </Flex>
    </Box>
  );
}
